// Islemonics dark theme ported verbatim. Single palette since LondaLab
// defaults to dark and never exposes a switcher.

// Warm near-black palette keyed to the LondaLab lockup artwork
// (background #050505, cream mark, sage tagline).
const palettes = {
  dark: {
    primary: '#211d1a',
    primaryHover: '#2b2622',
    primaryLight: '#e5d9c6',

    secondary: '#9db296',
    secondaryHover: '#b5c7ae',
    secondaryLight: '#c9d8c3',

    background: '#050505',
    backgroundSecondary: '#171412',
    backgroundTertiary: '#211d1a',

    surface: '#171412',
    surfaceHover: '#211d1a',
    surfaceActive: '#2b2622',

    text: '#ece7de',
    textSecondary: '#c7c0b4',
    textMuted: '#948d82',
    textInverse: '#ffffff',
    // Dark ink for text sitting on the light accent fills (sage/salmon
    // buttons); white fails contrast against those pastels.
    textOnAccent: '#1a1713',

    accent: '#d98a7f',
    accentHover: '#cf7466',
    success: '#9db296',
    successLight: '#b5c7ae',
    warning: '#d9b36a',
    warningLight: '#e5c98f',
    error: '#d98a7f',
    errorLight: '#e3a89f',
    info: '#8fa8bf',
    infoLight: '#aabfd2',

    border: '#2b2622',
    borderLight: '#211d1a',
    borderDark: '#37312c',

    overlay: 'rgba(255, 255, 255, 0.1)',
    overlayDark: 'rgba(0, 0, 0, 0.7)',

    shadow: 'rgba(0, 0, 0, 0.3)',
    shadowMedium: 'rgba(0, 0, 0, 0.5)',
    shadowStrong: 'rgba(0, 0, 0, 0.8)',
  },

};

const baseUnit = 8;
export const spacing = {
  xs: `${baseUnit * 0.5}px`,
  sm: `${baseUnit}px`,
  md: `${baseUnit * 2}px`,
  lg: `${baseUnit * 3}px`,
  xl: `${baseUnit * 4}px`,
  '2xl': `${baseUnit * 6}px`,
  '3xl': `${baseUnit * 8}px`,
  '4xl': `${baseUnit * 12}px`,
  '5xl': `${baseUnit * 16}px`,
  none: '0px',
};

export const fontFamilies = {
  primary: '"DM Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  secondary: '"DM Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  mono: '"JetBrains Mono", "Fira Code", "SF Mono", Monaco, Consolas, monospace',
};

export const fontWeights = {
  light: 300,
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
  extrabold: 800,
};

const sz = (m, t, d) => ({ mobile: m, tablet: t, desktop: d });
export const fontSizes = {
  xs: sz('0.75rem', '0.75rem', '0.875rem'),
  sm: sz('0.875rem', '0.875rem', '0.875rem'),
  base: sz('1rem', '1rem', '1rem'),
  lg: sz('1.125rem', '1.125rem', '1.25rem'),
  xl: sz('1.25rem', '1.375rem', '1.5rem'),
  '2xl': sz('1.5rem', '1.75rem', '2rem'),
  '3xl': sz('1.875rem', '2.25rem', '2.5rem'),
  '4xl': sz('2.25rem', '2.75rem', '3rem'),
  '5xl': sz('2.5rem', '3.5rem', '4rem'),
};

export const lineHeights = {
  tight: 1.2,
  snug: 1.375,
  normal: 1.5,
  relaxed: 1.625,
  loose: 2,
};

export const borderRadius = {
  none: '0px',
  sm: '4px',
  md: '8px',
  lg: '12px',
  xl: '16px',
  '2xl': '24px',
  full: '9999px',
};

const shadowsFor = (colors) => ({
  none: 'none',
  sm: `0 1px 3px 0 ${colors.shadow}, 0 1px 2px 0 ${colors.shadow}`,
  md: `0 4px 6px -1px ${colors.shadow}, 0 2px 4px -1px ${colors.shadow}`,
  lg: `0 10px 15px -3px ${colors.shadowMedium}, 0 4px 6px -2px ${colors.shadow}`,
  xl: `0 20px 25px -5px ${colors.shadowMedium}, 0 10px 10px -5px ${colors.shadow}`,
  '2xl': `0 25px 50px -12px ${colors.shadowStrong}`,
  inner: `inset 0 2px 4px 0 ${colors.shadow}`,
});

export const createTheme = (themeName = 'dark') => {
  const colors = palettes[themeName] || palettes.dark;
  return {
    name: themeName,
    colors,
    fonts: {
      families: fontFamilies,
      weights: fontWeights,
      sizes: fontSizes,
      lineHeights,
    },
    spacing: { scale: spacing },
    borderRadius,
    shadows: shadowsFor(colors),
  };
};

export const themeNames = {
  dark: 'Dark',
};

export const allThemes = Object.keys(palettes);

export const isValidTheme = (name) => Object.keys(palettes).includes(name);

export const defaultTheme = 'dark';
